/**
 * Sprint 24.5 — InstallWizardModal.
 *
 * The shared install-wizard shell + state machine. Renders the step indicator
 * (reused from Sprint 23.7) and the active step based on `template.wizard_steps`,
 * so one component drives every template:
 *   - M365:   confirm → authorize → toolsets → success
 *   - Custom: confirm → url-input → test → success
 *
 * Registration is lazy and idempotent: OAuth templates register when leaving
 * Confirm (so the authorize step has a connection id for Sprint 7.2's
 * connection-scoped flow); the dev Custom template registers at the test step.
 * The toolset step persists the final selection via connections/update-config.
 */

import { useState, type CSSProperties } from "react";
import { StepIndicator } from "../../welcome/StepIndicator";
import { showToast } from "../../primitives";
import { useAuth } from "../../../../lib/hooks/use-auth";
import { useUpdateConnectionConfig } from "../../../../hooks/use-connections";
import {
  type FirstPartyTemplate,
  type RegisterFromTemplateResult,
  useRegisterFromTemplate,
} from "../../../../hooks/use-first-party-templates";
import { InstallStepConfirm } from "./InstallStepConfirm";
import { InstallStepAuthorize } from "./InstallStepAuthorize";
import { InstallStepToolsets } from "./InstallStepToolsets";
import { InstallStepSuccess } from "./InstallStepSuccess";
import { InstallStepUrlInput } from "./InstallStepUrlInput";
import { InstallStepTest } from "./InstallStepTest";
import { EquipOnHiresModal } from "../EquipOnHiresModal";

export interface InstallWizardModalProps {
  template: FirstPartyTemplate;
  /** Fired when the connection is provisioned + the user is done. */
  onComplete: (result: RegisterFromTemplateResult) => void;
  /** Fired when the user cancels (returns to the Add menu). */
  onCancel: () => void;
}

export function InstallWizardModal({ template, onComplete, onCancel }: InstallWizardModalProps) {
  const { user } = useAuth();
  const registerMut = useRegisterFromTemplate();
  const updateConfig = useUpdateConnectionConfig();

  const steps = template.wizard_steps;
  const [stepIndex, setStepIndex] = useState(0);
  const [registered, setRegistered] = useState<RegisterFromTemplateResult | null>(null);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [customUrl, setCustomUrl] = useState("");
  const [toolCount, setToolCount] = useState(0);
  const [selectedToolsets, setSelectedToolsets] = useState<string[]>(
    (template.toolsets ?? []).filter((t) => t.default_enabled).map((t) => t.id),
  );
  const [equipOpen, setEquipOpen] = useState(false);

  const step = steps[stepIndex];
  const isOAuthFlow = steps.includes("authorize");

  function goNext() {
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }

  /** Register from the template once; idempotent. Returns ok + the connection status. */
  async function ensureRegistered(extra?: { url?: string }): Promise<{ ok: boolean; error?: string }> {
    if (connectionId) return { ok: true };
    try {
      const result = await registerMut.mutateAsync({
        template_slug: template.slug,
        ...(template.toolsets ? { enabled_toolsets: selectedToolsets } : {}),
        ...(extra?.url ? { url: extra.url } : {}),
      });
      setRegistered(result);
      setConnectionId(result.connection_id);
      const conn = result.connection ?? undefined;
      if (typeof conn?.tool_count === "number") setToolCount(conn.tool_count);
      if (conn?.status === "error") {
        return { ok: false, error: "The MCP server returned an error during discovery." };
      }
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : "Registration failed." };
    }
  }

  async function handleConfirmContinue() {
    if (isOAuthFlow) {
      // OAuth flow needs the connection to exist before the authorize step.
      const res = await ensureRegistered();
      if (!res.ok) {
        showToast(res.error ?? "Couldn't start the connection.", "error");
        return;
      }
    }
    goNext();
  }

  async function handleToolsetsContinue() {
    if (connectionId) {
      try {
        const { connection } = await updateConfig.mutateAsync({
          connection_id: connectionId,
          enabled_toolsets: selectedToolsets,
        });
        if (typeof connection.tool_count === "number") setToolCount(connection.tool_count);
      } catch {
        showToast("Couldn't save toolset selection — you can adjust it on the detail page.", "error");
      }
    }
    goNext();
  }

  function toggleToolset(id: string) {
    setSelectedToolsets((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  }

  function finish() {
    const result: RegisterFromTemplateResult =
      registered ?? {
        connection_kind: template.type,
        connection_id: connectionId,
        template_slug: template.slug,
      };
    onComplete(result);
  }

  const toolsets = template.toolsets ?? [];

  return (
    <div style={backdrop} onClick={onCancel}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <StepIndicator current={stepIndex + 1} total={steps.length} />

        {step === "confirm" && (
          <InstallStepConfirm
            template={template}
            onContinue={handleConfirmContinue}
            onCancel={onCancel}
            busy={registerMut.isPending}
          />
        )}

        {step === "authorize" && (
          <InstallStepAuthorize
            template={template}
            connectionId={connectionId}
            onAuthorized={goNext}
            onCancel={onCancel}
          />
        )}

        {step === "toolsets" && (
          <InstallStepToolsets
            template={template}
            selected={selectedToolsets}
            onToggle={toggleToolset}
            onContinue={handleToolsetsContinue}
            busy={updateConfig.isPending}
          />
        )}

        {step === "url-input" && (
          <InstallStepUrlInput
            template={template}
            url={customUrl}
            onUrlChange={setCustomUrl}
            onContinue={goNext}
            onCancel={onCancel}
          />
        )}

        {step === "test" && (
          <InstallStepTest
            template={template}
            runTest={() => ensureRegistered({ url: customUrl })}
            onTested={goNext}
            onCancel={onCancel}
          />
        )}

        {step === "success" && (
          <InstallStepSuccess
            template={template}
            toolCount={toolCount}
            toolsetsEnabled={toolsets.length > 0 ? selectedToolsets.length : undefined}
            toolsetsTotal={toolsets.length > 0 ? toolsets.length : undefined}
            authorizedAsEmail={isOAuthFlow ? user?.email ?? null : null}
            onEquip={() => setEquipOpen(true)}
            onDone={finish}
          />
        )}
      </div>

      {equipOpen && connectionId && (
        <EquipOnHiresModal
          open={equipOpen}
          connectionKind={template.type}
          connectionId={connectionId}
          connectionName={template.display_name}
          onClose={() => {
            setEquipOpen(false);
            finish();
          }}
        />
      )}
    </div>
  );
}

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
};

const card: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-lg)",
  width: 480,
  maxWidth: "90vw",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  fontFamily: "var(--font-sans)",
  boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
};
